import { Tooltip } from '@material-ui/core';
import { useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import ContentCopyIcon from '@material-ui/icons/ContentCopy';

type CopyableValue = string | number | Record<string, unknown>;

interface CopyIconButtonProps {
  /**
   * What to copy. Can be:
   * - a string
   * - a number
   * - an object of label: value pairs(will be converted to a string with key-value lines)
   */
  dataToCopy: CopyableValue;
  /**Tooltip label showing before copying */
  label?: string;
  /**Size of the icon button */
  size?: 'small' | 'medium';
  /**Duration to show the "copied" confirmation tooltip after copying */
  confirmDurationMs?: number;
  /**Optional className for positioning/styling the icon button from the parent */
  className?: string;
  color?: 'inherit' | 'default' | 'primary' | 'secondary';
  onCopy?: () => void;
}

const formatValueForCopy = (value: CopyableValue): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value.toString();
  }
  return Object.entries(value)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
};

const CopyIconButton = ({
  dataToCopy,
  onCopy,
  confirmDurationMs,
  label = 'Copy to clipboard',
  size = 'small',
  color = 'default',
}: CopyIconButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(formatValueForCopy(dataToCopy));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), confirmDurationMs ?? 2000);
      if (onCopy) onCopy();
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Tooltip title={isCopied ? 'Copied!' : label}>
      <IconButton
        aria-label={isCopied ? 'Copied!' : label}
        onClick={handleCopy}
        size={size || 'small'}
        color={color}
      >
        <ContentCopyIcon fontSize={size} />
      </IconButton>
    </Tooltip>
  );
};

export default CopyIconButton;
